import { BlockId, blockPropsMap } from '../game/defs/block';
import { BlockDef, MapDef } from '../game/defs/map';
import { blockViewAssets } from '../game/util/blockTools';
import { BasicScreen } from '../lib/ui/BasicScreen';
import { BasicTabMenu } from '../lib/ui/BasicTabMenu';
import { EditorBlockMenu } from './components/block/EditorBlockMenu';
import {
  EditorGoalMenu,
  EditorGoalMenuOptions,
} from './components/goal/EditorGoalMenu';
import { EditorGrid } from './components/grid/EditorGrid';
import { EditorHeader } from './components/header/EditorHeader';
import { EditorSpawnMenu } from './components/spawn/EditorSpawnMenu';
import appConfig from '../defs/appConfig';
import Match2EditorScene from 'src/game/scenes/Match2EditorScene';
import sounds from 'src/lib/sounds';
import {
  EditorMiscMenu,
  EditorMiscMenuOptions,
} from './components/misc/EditorMiscMenu';
import { PuzzleMapExporter } from '../game/util/PuzzleMapExporter';
import { PuzzleMapImporter } from '../game/util/PuzzleMapImporter';
import {
  clearEditorOverride,
  getEditorMaxLevel,
  getEditorOverride,
  getEditorOverrides,
  getPuzzleMap,
  setEditorOverride,
} from '../match2.assets';
import {
  editorDownloadJson,
  editorGetStartingPowerups,
  editorPlayGame,
  editorSetLocalStorageLevel,
  editorUploadJson,
  getEmptyMapDef,
  validateLevels,
} from './editor.utils';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { match2MaxLevel } from 'src/replicant/match2/match2.ruleset';
import { decodeSaveStateData } from '../savestate/match2-savestate.data';
import { user } from 'src/StateObserver';
import { waitFor } from '../lib/util/waitFor';

// types
//-----------------------------------------------------------------------------
export enum TabIndex {
  blocks,
  spawns,
  goals,
}

export interface MapDefEntry {
  data: string;
  level: number;
  blockGen?: string;
}

let loadedFromLocalStorage = false;

/*
    puzzle editor screen
*/
export class EditorScreen extends BasicScreen {
  // fields
  //-------------------------------------------------------------------------
  // scene
  private _header!: EditorHeader;
  private _tabMenu!: BasicTabMenu;
  private _grid!: EditorGrid;

  // properties
  //-------------------------------------------------------------------------
  public get tab(): TabIndex {
    return this._tabMenu.tab;
  }

  public get tabMenu(): BasicTabMenu {
    return this._tabMenu;
  }

  public get header(): EditorHeader {
    return this._header;
  }

  public get blockMenu(): EditorBlockMenu {
    return this._tabMenu.controller.tabs[TabIndex.blocks]
      .screen as EditorBlockMenu;
  }

  public get spawnMenu(): EditorSpawnMenu {
    return this._tabMenu.controller.tabs[TabIndex.spawns]
      .screen as EditorSpawnMenu;
  }

  public get goalMenu(): EditorGoalMenu {
    return this._tabMenu.controller.tabs[TabIndex.goals]
      .screen as EditorGoalMenu;
  }

  public get grid(): EditorGrid {
    return this._grid;
  }

  // impl
  //-------------------------------------------------------------------------
  public assets() {
    return [
      ...BasicTabMenu.assets(),
      ...EditorGrid.assets(),
      ...EditorBlockMenu.assets(),
      ...EditorSpawnMenu.assets(),
      ...EditorGoalMenu.assets(),
      ...Object.keys(blockPropsMap)
        .map((id: string) => blockViewAssets(id as BlockId))
        .flat(),
    ];
  }

  constructor(public readonly scene: Match2EditorScene) {
    super();
    scene.getView().addSubview(this.view.view);
  }

  public async init() {
    // No music in editor.
    sounds.music.setMuted(true);

    // spawn header
    this._spawnHeader();

    // spawn grid
    this._spawnGrid();

    // spawn tab menu
    this._spawnTabMenu();

    // Load the puzzleMaps.json from local storage, if any, only first time in session.
    if (!loadedFromLocalStorage) {
      loadedFromLocalStorage = true;
      await this.loadFromLocalStorage();
    } else {
      this.checkSaved();
    }

    this.playSavedLevelIfAvailable();
  }

  // api
  //-------------------------------------------------------------------------
  public getMapDef(): MapDef {
    // create combined map def from corresponding views
    return {
      moves: this.goalMenu.moves,
      goals: this.goalMenu.goals,
      spawns: this.spawnMenu.spawns,
      grid: this.grid.grid,
    };
  }

  public setMapDef(mapDef: MapDef): void {
    // Force export/import of the data, to check if created data can be handled by the game.
    // In case of inconsistencies, that can be spotted here, instead only after importing the
    // generated puzzleMaps.json file into the game.
    const raw = new PuzzleMapExporter(mapDef).export();

    // Don't require goals for the editor, as the level might have been cleared.
    const decoded = new PuzzleMapImporter(raw).import(false);

    if (!decoded) {
      throw new Error('Invalid map def!');
    }

    this.grid.grid = decoded.grid;
    this.spawnMenu.spawns = decoded.spawns;
    this.goalMenu.moves = decoded.moves;
    this.goalMenu.goals = decoded.goals;

    // Apply dymamic options to the block buttons
    this.blockMenu.updateDynamicOptions(this);
  }

  public clear() {
    this.grid.clear();
    this.goalMenu.clear();
    this.header.setAsUnsaved();
  }

  // private: spawn
  //-------------------------------------------------------------------------

  private _spawnHeader() {
    this.root.addChild(
      (this._header = new EditorHeader({
        width: appConfig.size.width,
        height: 60,
        scene: this.scene,
      })),
    );
  }

  private _spawnGrid() {
    // create
    this._grid = this.root.addChild(new EditorGrid());
    this._grid.x = 20;
    this._grid.y = 60;

    this._grid.onCellTap = (column, row) => {
      const selectedButton = this.blockMenu.selectedButton;
      if (selectedButton) {
        switch (selectedButton.type) {
          case 'block': {
            const selectedBlockDef = selectedButton.def;
            this._placeBlock({
              column,
              row,
              id: selectedBlockDef.id,
              def: selectedBlockDef,
            });
            break;
          }
          case 'goal':
            // Do nothing?
            break;
          case 'clear':
            // Clear and disable the cell
            this._grid.clearCell(column, row, true);
            this.setAsUnsaved();
            this.goalMenu.autoUpdate(this);
            break;
          case 'tile':
            // Clear and enable the cell
            this._grid.clearCell(column, row);
            this.setAsUnsaved();
            this.goalMenu.autoUpdate(this);
            break;
          default:
            selectedButton.type satisfies never;
        }
      }
    };
  }

  private _placeBlock(args: {
    column: number;
    row: number;
    id: BlockId;
    def?: BlockDef;
  }) {
    this._grid.placeBlock(args.column, args.row, args.id, args.def);
    this.setAsUnsaved();
    this.goalMenu.autoUpdate(this);
  }

  private _spawnTabMenu() {
    // create tab menu
    this._tabMenu = new BasicTabMenu({
      width: appConfig.size.width,
      height: 320,
      color: 0x002266,
      border: 0,
      spacing: 2,
      alpha: 1,
      firstTab: 0,
      tabs: [
        {
          text: 'BLOCKS',
          screen: EditorBlockMenu,
        },
        {
          text: 'SPAWNS',
          screen: EditorSpawnMenu,
          options: {
            screen: this,
          } as EditorGoalMenuOptions,
        },
        {
          text: 'GOALS',
          screen: EditorGoalMenu,
          options: {
            screen: this,
          } as EditorGoalMenuOptions,
        },
        {
          text: 'MISC',
          screen: EditorMiscMenu,
          options: {
            screen: this,
          } as EditorMiscMenuOptions,
        },
      ],
    });
    this._tabMenu.x = 0;
    this._tabMenu.y = this.grid.y + this.grid.height + 6;
    this.root.addChild(this._tabMenu);
  }

  public async getPuzzleMapData(): Promise<MapDefEntry[]> {
    setEditorOverride(this.scene.level, this.scene.editor!.getMapDef());
    const promises = [];
    for (let i = 1; i <= getEditorMaxLevel(); i++) {
      promises.push(getPuzzleMap(i));
    }

    const mapDefs = await Promise.all(promises);

    // Run validation
    if (!validateLevels(mapDefs)) {
      throw new Error('Could not validate levels');
    }

    const data = mapDefs.map((mapDef) => ({
      mapDef: new PuzzleMapExporter(mapDef).export(),
      blockGen: mapDef.blockGen,
    }));

    // Convert the data list to an array of objects like the original puzzleMaps.json:
    // a list of {level, data, blockGen?} entries.
    const puzzleMaps = data.map(({ mapDef: data, blockGen }, index) => {
      const level = index + 1;
      const entry: MapDefEntry = {
        data,
        level,
        blockGen,
      };

      // Remove the property if it is undefined
      if (!entry.blockGen) {
        delete entry.blockGen;
      }
      return entry;
    });

    return puzzleMaps;
  }

  public playCurrentLevel() {
    editorPlayGame(
      this.scene.level,
      this.getMapDef(),
      editorGetStartingPowerups(),
    );
  }

  public async playOriginalLevel() {
    const level = this.scene.level;
    if (level > match2MaxLevel) {
      window.alert(
        `Level ${level} is not available in the original game.\nMax level is ${match2MaxLevel}`,
      );
      return;
    }
    const originalMapDef = await getPuzzleMap(level, true);
    await editorPlayGame(
      level,
      originalMapDef,
      editorGetStartingPowerups(),
      this.getMapDef(), // Replace map overriden on gameplay by the current editing map
    );
  }

  private async playSavedLevelIfAvailable() {
    // Wait a little bit for things set
    await waitFor(100);

    const data = user().match2.saveState?.data;

    // Skip if there is no save state available
    if (!data) return;

    const state = decodeSaveStateData(data);

    // Skip if state could not be decoded
    if (!state) return;

    const mapDef = new PuzzleMapImporter(state.mapDef).import();

    // Skip if map could not be imported
    if (!mapDef) return;

    // Play the game with the saved state
    editorPlayGame(
      this.scene.level,
      mapDef,
      editorGetStartingPowerups(),
      this.getMapDef(), // Replace map overriden on gameplay by the current editing map
    );
  }

  // === LOCAL STORAGE LOAD & SAVE ============================================

  public async saveInLocalStorage() {
    const puzzleMaps = await this.getPuzzleMapData();
    const rawData = JSON.stringify(puzzleMaps);
    localStorage.setItem('puzzleMaps', rawData);
    editorSetLocalStorageLevel(this.scene.level);
    this.header.setAsSaved();

    return true;
  }

  public async loadFromLocalStorage() {
    const rawData = localStorage.getItem('puzzleMaps');
    if (!rawData) return;
    const puzzleMaps = JSON.parse(rawData) as MapDefEntry[];

    puzzleMaps.forEach((entry, index) => {
      setEditorOverride(index + 1, new PuzzleMapImporter(entry.data).import()!);
    });
    this.header.setAsSaved();
  }

  // === UPLOAD & DOWNLOAD ====================================================

  public async uploadPuzzleMaps() {
    const obj = await editorUploadJson();

    if (!obj) {
      window.alert('UPDLOAD FAILED! Invalid file');
      return;
    }

    if (!Array.isArray(obj)) {
      window.alert('UPDLOAD FAILED! The file should have multiple levels');
      return;
    }

    const entryList = obj as MapDefEntry[];
    const dataList = entryList.map((entry, index) => {
      const data = new PuzzleMapImporter(entry.data).import();
      if (!data) console.log('Invalid level data: ' + (index + 1));
      return data;
    });

    if (dataList.some((data) => !data)) {
      window.alert('UPDLOAD FAILED! File contains invalid level data');
      return;
    }

    dataList.forEach(async (data, index) => {
      setEditorOverride(index + 1, data!);
    });
    this.scene.setLevel(this.scene.level);
    this.header.setAsUnsaved();
  }

  public async downloadPuzzleMaps() {
    // Auto-save in local storage if downloading current work
    const ok = await this.saveInLocalStorage();

    // If failed to save in local storage, there is something wrong with the levels. Abort.
    if (!ok) return;

    const puzzleMaps = await this.scene.editor.getPuzzleMapData();
    editorDownloadJson('puzzleMaps', puzzleMaps);
  }

  public async uploadSinglePuzzleMap() {
    const obj = await editorUploadJson();

    if (!obj) {
      window.alert('UPDLOAD FAILED! Invalid file');
      return;
    }

    if (Array.isArray(obj)) {
      window.alert('UPDLOAD FAILED! The file should have only a single level');
      return;
    }

    const entry = obj as MapDefEntry;
    const data = new PuzzleMapImporter(entry.data).import();

    if (!data) {
      window.alert('UPDLOAD FAILED! Invalid level data in uploaded file');
      return;
    }

    setEditorOverride(this.scene.level, data);
    this.scene.setLevel(this.scene.level);
    this.header.setAsUnsaved();
  }

  public async downloadSinglePuzzleMap() {
    const puzzleMaps = await this.scene.editor.getPuzzleMapData();
    const level = this.scene.level;
    const map = puzzleMaps[level - 1];
    const sufix = String(level).padStart(4, '0');
    editorDownloadJson(`puzzleMap_lv${sufix}`, map);
  }

  private async checkSaved() {
    const puzzleMaps = await this.getPuzzleMapData();
    const current = JSON.stringify(puzzleMaps);
    const saved = localStorage.getItem('puzzleMaps');

    // Compare current and saved puzzle maps to see if they are different
    if (current === saved) {
      this.header.setAsSaved();
    } else {
      this.header.setAsUnsaved();
    }
  }

  public setAsSaved() {
    this.header.setAsSaved();
  }

  public setAsUnsaved() {
    this.header.setAsUnsaved();
  }

  public copy() {
    const mapDef = this.getMapDef();

    if (!validateLevels([{ ...mapDef, level: this.scene.level }])) {
      return;
    }

    const exporter = new PuzzleMapExporter(mapDef);
    const text = exporter.export();
    navigator.clipboard.writeText(text);
  }

  public async paste(text?: string) {
    if (!text) {
      text = await navigator.clipboard.readText();
    }

    // Try to parse as JSON
    try {
      const mapDef = JSON.parse(text!);
      this.setMapDef(mapDef);
      this.header.setAsUnsaved();
      return;
    } catch (e) {
      // Not JSON, try to parse as map encoding.
    }

    if (text) {
      const importer = new PuzzleMapImporter(text);
      const mapDef = importer.import();
      if (mapDef) {
        this.setMapDef(mapDef);
        this.header.setAsUnsaved();
      } else {
        console.error('Unable to import map:', text);
      }
    } else {
      console.error('No text in clipboard!');
    }
  }

  public openCheatsMenu() {
    openPopupPromise('popupMatch2Cheats', {
      gameplay: false,
      editor: true,
    });
  }

  /**
   * Change the level to be edited. If the level is higher than the max level, generate new levels up to the requested level.
   */
  public async changeLevel(level: number) {
    // Set current level as the override
    setEditorOverride(this.scene.level, this.scene.editor!.getMapDef());

    // Flag to indicate if new levels were generated
    let hasGeneratedNewLevels = false;

    const maxLevel = getEditorMaxLevel();

    // If the level is higher than the max level, generate new levels up to the requested level.
    if (level > maxLevel) {
      for (let i = maxLevel + 1; i <= level; i++) {
        // In case there is a level override aready, skip it
        if (getEditorOverride(i)) continue;

        // Generate a new puzzle map for the level
        const mapDef = getEmptyMapDef();
        setEditorOverride(i, mapDef);
        hasGeneratedNewLevels = true;
      }
    }

    // Set the level
    this.scene.setLevel(level);

    // Mark as unsaved if new levels were generated
    if (hasGeneratedNewLevels) {
      this.header.setAsUnsaved();
    }
  }

  /**
   * Validate all overriden levels
   */
  public async validateOverridenLevels() {
    const mapDefs: Array<MapDef & { level?: number }> = [];
    for (const [key, value] of getEditorOverrides()) {
      mapDefs.push({ ...value, level: key });
    }
    const allValid = validateLevels(mapDefs);
    if (allValid) window.alert('All levels are valid!');
  }

  /**
   * Clear all production levels to the original levels.
   */
  public resetProdLevels() {
    const ok = window.confirm(`Reset level 1 to ${match2MaxLevel}.\nConfirm?`);
    if (!ok) return;
    for (let i = 1; i <= match2MaxLevel; i++) {
      clearEditorOverride(i);
    }
    this.saveInLocalStorage();
  }

  /**
   * Clear all saved levels and reload the page.
   */
  public resetEditor() {
    const ok = window.confirm(`Fully reset the editor.\nConfirm?`);
    if (!ok) return;
    localStorage.removeItem('puzzleMaps');
    localStorage.removeItem('editorLevel');
    window.location.reload();
  }
}
